/**
 * Application Constants
 * Centralized constants to avoid magic strings and numbers throughout the codebase
 */

// Order Status Constants
export const ORDER_STATUS = {
  PENDING: 'pending',
  IN_PROGRESS: 'in_progress',
  CONFIRMED: 'confirmed',
  PREPARING: 'preparing',
  PREPARED: 'prepared',
  OUT_FOR_DELIVERY: 'out_for_delivery',
  DELIVERED: 'delivered',
  READY_FOR_PICKUP: 'ready_for_pickup',
  COMPLETED: 'completed',
  CANCELLED: 'cancelled'
};

// Order Type Constants
export const ORDER_TYPE = {
  DELIVERY: 'delivery',
  TAKEAWAY: 'takeaway',
  DINE_IN: 'dine_in'
};

// Payment Method Constants
export const PAYMENT_METHOD = {
  COD: 'cod',
  ONLINE: 'online',
  CARD: 'card',
  UPI: 'upi'
};

// Payment Status Constants
export const PAYMENT_STATUS = {
  PENDING: 'pending',
  PAID: 'paid',
  FAILED: 'failed',
  REFUNDED: 'refunded'
};

// Customer Tag Constants
export const CUSTOMER_TAG = {
  NEW: 'new',
  REGULAR: 'regular',
  VIP: 'vip'
};

// Customer Tag Thresholds
export const CUSTOMER_TAG_THRESHOLDS = {
  VIP_ORDERS: 11,
  VIP_SPENT: 5000,
  REGULAR_ORDERS: 3
};

// Order Source Constants
export const ORDER_SOURCE = {
  WEB: 'web',
  PHONE: 'phone',
  MOBILE_APP: 'mobile_app',
  ADMIN: 'admin'
};

// Filter Type Constants
export const FILTER_TYPE = {
  ALL: 'all',
  VEG: 'veg',
  NON_VEG: 'nonveg'
};

// Delivery Speed Constants (in km/h)
export const DELIVERY_SPEED = {
  SLOW: 15,      // Slow traffic
  NORMAL: 20,    // Normal traffic
  FAST: 25       // Light traffic
};

// Order Timing Constants (in minutes)
export const ORDER_TIMING = {
  PREPARATION_TIME: 25,
  BUFFER_TIME: 5,
  DELIVERY_ESTIMATE: '30-45 mins'
};

// UI Constants
export const UI_CONSTANTS = {
  HEADER_OFFSET: 140,           // Scroll offset for fixed headers
  DEBOUNCE_DELAY: 300,          // Debounce delay for search inputs (ms)
  SEARCH_MIN_LENGTH: 2,         // Minimum search query length
  MAX_FAVORITE_ITEMS: 3,        // Max favorite items to display
  ITEMS_PER_PAGE: 20,           // Pagination items per page
  TOAST_DURATION: 3000,         // Toast notification duration (ms)
  MODAL_ANIMATION_DURATION: 300 // Modal animation duration (ms)
};

// Validation Constants
export const VALIDATION = {
  MIN_ORDER_AMOUNT: 200,
  MAX_PHONE_LENGTH: 15,
  MIN_PHONE_LENGTH: 10,
  MAX_NAME_LENGTH: 100,
  MAX_ADDRESS_LENGTH: 500,
  MAX_TEXT_LENGTH: 2000,
  MAX_STRING_LENGTH: 1000,
  MAX_EMAIL_LENGTH: 254,
  MAX_FILE_SIZE: 5 * 1024 * 1024, // 5MB in bytes
  ALLOWED_IMAGE_TYPES: ['image/jpeg', 'image/jpg', 'image/png', 'image/webp']
};

// Indian Phone Number Regex
export const INDIAN_PHONE_REGEX = /^[6-9]\d{9}$/;

// Firebase Collection Names
export const COLLECTIONS = {
  MENU_ITEMS: 'menu_items',
  ORDERS: 'orders',
  CUSTOMERS: 'customers',
  CUSTOMER_ACCOUNTS: 'customer_accounts',
  ADMIN_USERS: 'admin_users',
  SETTINGS: 'settings',
  APP_SETTINGS: 'app_settings',
  COUPONS: 'coupons',
  REVIEWS: 'reviews',
  ANALYTICS: 'analytics',
  NOTIFICATIONS: 'notifications'
};

// Local Storage Keys
export const STORAGE_KEYS = {
  ADMIN_EMAIL: 'adminEmail',
  CART_ITEMS: 'cartItems',
  USER_PREFERENCES: 'userPreferences',
  THEME: 'theme'
};

// Error Messages
export const ERROR_MESSAGES = {
  NETWORK_ERROR: 'Network error. Please check your internet connection.',
  FIREBASE_ERROR: 'Database error. Please try again later.',
  PAYMENT_ERROR: 'Payment failed. Please try again.',
  VALIDATION_ERROR: 'Please check your input and try again.',
  UPLOAD_ERROR: 'File upload failed. Please try again.',
  AUTH_ERROR: 'Authentication failed. Please login again.',
  PERMISSION_ERROR: 'You do not have permission to perform this action.',
  NOT_FOUND: 'The requested resource was not found.',
  SERVER_ERROR: 'Server error. Please try again later.'
};

// Success Messages
export const SUCCESS_MESSAGES = {
  ORDER_PLACED: 'Order placed successfully!',
  ORDER_UPDATED: 'Order updated successfully!',
  PAYMENT_SUCCESS: 'Payment completed successfully!',
  PROFILE_UPDATED: 'Profile updated successfully!',
  ITEM_ADDED: 'Item added successfully!',
  ITEM_UPDATED: 'Item updated successfully!',
  ITEM_DELETED: 'Item deleted successfully!'
};

// App Metadata
export const APP_METADATA = {
  NAME: 'Odisha Zaika',
  TAGLINE: 'Authentic Odia Cuisine',
  DEFAULT_PHONE: '7873611363',
  DEFAULT_EMAIL: 'contact@odishazaika.com',
  DEFAULT_ADDRESS: 'Bhubaneswar, Odisha, India',
  BRAND_COLOR: '#FFC124'
};

// API Endpoints (if any backend APIs are added)
export const API_ENDPOINTS = {
  VERIFY_PAYMENT: '/api/verify-payment',
  SEND_OTP: '/api/send-otp',
  VERIFY_OTP: '/api/verify-otp'
};

// Social Media Links (for future use)
export const SOCIAL_LINKS = {
  FACEBOOK: '',
  INSTAGRAM: '',
  TWITTER: '',
  YOUTUBE: ''
};

// Days of Week
export const DAYS_OF_WEEK = [
  'monday',
  'tuesday',
  'wednesday',
  'thursday',
  'friday',
  'saturday',
  'sunday'
];

// Coupon Types
export const COUPON_TYPE = {
  PERCENTAGE: 'percentage',
  FLAT: 'flat',
  FREE_DELIVERY: 'free_delivery'
};

// Admin Roles
export const ADMIN_ROLES = {
  SUPER_ADMIN: 'super_admin',
  ADMIN: 'admin',
  MANAGER: 'manager',
  STAFF: 'staff',
  DELIVERY: 'delivery'
};

// Chart Colors (for analytics)
export const CHART_COLORS = {
  PRIMARY: '#FFC124',
  SECONDARY: '#FF6B6B',
  SUCCESS: '#51CF66',
  WARNING: '#FFD43B',
  ERROR: '#FF6B6B',
  INFO: '#4DABF7'
};

// Export all constants as a single object for convenience
const constants = {
  ORDER_STATUS,
  ORDER_TYPE,
  PAYMENT_METHOD,
  PAYMENT_STATUS,
  CUSTOMER_TAG,
  CUSTOMER_TAG_THRESHOLDS,
  ORDER_SOURCE,
  FILTER_TYPE,
  DELIVERY_SPEED,
  ORDER_TIMING,
  UI_CONSTANTS,
  VALIDATION,
  INDIAN_PHONE_REGEX,
  COLLECTIONS,
  STORAGE_KEYS,
  ERROR_MESSAGES,
  SUCCESS_MESSAGES,
  APP_METADATA,
  API_ENDPOINTS,
  SOCIAL_LINKS,
  DAYS_OF_WEEK,
  COUPON_TYPE,
  ADMIN_ROLES,
  CHART_COLORS
};

export default constants;
